import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';
import { ButtonIconCondensed } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';
import { useCurrentUser } from 'javascripts/hooks';
import { hasRoleAccess, ADMIN, MANAGER } from 'helpers/roles';
import { useModal } from 'components/shared/hooks';
import { PROJECT_TASK_SOURCE } from 'data/v3';
import ProjectTaskAssets from './ProjectTaskAssets';
import CompletedByInfo from 'components/projects/taskList/components/CompletedByInfo';
import useEditProjectTask from './hooks/useEditProjectTask';
import useDeleteProjectTask from './hooks/useDeleteProjectTask';
import useProjectTaskUpdateStatus from './hooks/useProjectTaskUpdateStatus';

// #region styled-components
const TaskContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xs);
  width: 100%;
  padding: var(--cc_size_spacing_s);
  border: 1px solid transparent;
  border-radius: var(--cc_size_border_radius_l);

  ${(props) =>
    props.isFocused
      ? `border-color: var(--cc_color_border_input_active);`
      : null}

  &:hover {
    background-color: var(--cc_color_background_2);
  }
`;

const TaskRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--cc_size_spacing_s);
`;

const TaskCompleteCircle = styled.div`
  position: relative;
  top: 1px;
  display: flex;
  flex: none;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border: var(--cc_size_border_width_m) solid var(--cc_color_border_default);
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  background: var(--cc_color_utility_success);

  i {
    color: white;
    font-size: var(--cc_size_text_s);
  }
`;

const TaskIncompleteCircle = styled.div`
  position: relative;
  top: 1px;
  flex: none;
  width: 22px;
  height: 22px;
  border: var(--cc_size_border_width_m) solid
    var(--cc_color_border_input_inactive);
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? '0.25' : '1')};

  &:hover {
    border-color: var(--cc_color_text_nonessential);
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xxs);
  width: 100%;
`;

// Using div here to allow for text input to grow with content
const TaskText = styled.div`
  background-color: transparent;
  border: none;
  box-sizing: border-box;
  resize: none;
  min-width: 1px;
  padding: 0;
  overflow: hidden;
  margin-top: ${tokens.size_spacing_xxs}px;
  height: auto;
  font-family: 'Averta', system-ui, sans-serif;
  font-size: calc(var(--cc_size_text_s) + 1px);
  color: var(--cc_color_text_default);
  line-height: 1.4;
  outline: none;
  max-width: calc(100% - 32px);
  white-space: pre-line;
  cursor: text;
`;

const TextRow = styled.div`
  position: relative;
  display: flex;
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  height: fit-content;
  flex: none;
`;
// #endregion

const ProjectTask = ({
  task,
  index,
  disabled,
  isPhotoView,
  onTaskAssetClick,
  createProjectTask,
  addNewTask,
  removeNewTask,
}) => {
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [inputIsFocused, setInputIsFocused] = useState(false);
  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const { showModal, hideModal, Modal } = useModal();

  const textRef = useRef(null);

  const currentUser = useCurrentUser();
  const isAdmin = hasRoleAccess(currentUser, [ADMIN, MANAGER]);

  const setCursorToEnd = (el) => {
    const range = document.createRange();
    const sel = window.getSelection();

    range.selectNodeContents(el);
    range.collapse(false);

    sel.removeAllRanges();
    sel.addRange(range);
  };

  useEffect(() => {
    if (inputIsFocused) {
      textRef.current.focus();
      setCursorToEnd(textRef.current);
    }
  }, [inputIsFocused]);

  useEffect(() => {
    if (task.isNew && textRef.current.innerText === '') {
      setInputIsFocused(true);
    }
  }, [task]);

  useEffect(() => {
    if (isEnterPressed) {
      textRef.current.blur();
    }
  }, [isEnterPressed]);

  // A task currently will never have both images and videos
  // This may change in the future and should be updated accordingly
  const assetSource = task.images.nodes.length
    ? PROJECT_TASK_SOURCE.PHOTO
    : PROJECT_TASK_SOURCE.VIDEO;

  const { editProjectTask } = useEditProjectTask({
    taskId: task.id,
    source: isPhotoView ? assetSource : PROJECT_TASK_SOURCE.PROJECT,
  });

  const { deleteProjectTask } = useDeleteProjectTask({
    taskId: task.id,
    source: isPhotoView ? assetSource : PROJECT_TASK_SOURCE.PROJECT,
  });

  const { toggleProjectTaskStatus } = useProjectTaskUpdateStatus({
    taskId: task.id,
    currentStatus: task.completed,
    source: isPhotoView ? assetSource : PROJECT_TASK_SOURCE.PROJECT,
  });

  const hideButtons = () => {
    setButtonsVisible(false);
  };

  const onDeletePress = () => {
    hideButtons();
    showModal(Modal.CONFIRMATION, {
      onRequestClose: hideModal,
      confirmAction: () => {
        deleteProjectTask();
        hideModal();
      },
      confirmLabel: translate('common.confirmDelete'),
      cancelLabel: translate('common.cancelDelete'),
      message: translate('projectTask.deleteModal.confirm'),
      title: translate('projectTask.deleteModal.title'),
    });
  };

  // Adds a new task if either create button is clicked or enter is pressed
  const handleAddNewTask = ({ isBlurredByEnter }) => {
    // Don't add new task if the task is already completed
    if (task.completed) {
      return;
    }

    if (isBlurredByEnter) {
      addNewTask({ index: index + 1, position: task.position + 1 });
      setIsEnterPressed(false);
      return;
    }

    // Remove new task if the user clicks away without creating a new task
    removeNewTask();
  };

  const handleEmptyTask = () => {
    if (task.isNew) {
      removeNewTask();
      return;
    }

    onDeletePress();
    textRef.current.innerText = task.details;
  };

  // Handles creating and editing of tasks
  const onTextBlur = (e) => {
    setInputIsFocused(false);

    const textValue = e.target.textContent;
    const taskIsEmpty = textValue.trim() === '';

    if (taskIsEmpty) {
      handleEmptyTask();
      return;
    }

    if (task.isNew) {
      createProjectTask({
        details: textValue,
        position: task.position,
      });
    } else if (textValue !== task.details) {
      editProjectTask({
        details: textValue,
      });
    }

    handleAddNewTask({
      isBlurredByEnter: isEnterPressed,
    });
  };

  const showButtons = () => {
    if (isAdmin) {
      setButtonsVisible(true);
    }
  };

  const taskImages = task?.images ?? [];
  const taskVideos = task?.videos ?? [];

  // Editable for admins/managers of if the task is created by a restricted users
  const userCanEdit = isAdmin || !!task.isNew;

  return (
    <TaskContainer
      isFocused={inputIsFocused}
      onMouseOver={showButtons}
      onMouseLeave={hideButtons}
      // onMouseDown is used to drag the task for rearranging
      onMouseUp={() => setInputIsFocused(true)}
    >
      <TaskRow
        onFocus={showButtons} // For keyboard accessibility
      >
        {task.completed ? (
          <TaskCompleteCircle
            onClick={toggleProjectTaskStatus}
            disabled={disabled}
          >
            <i className="mdi mdi-check" />
          </TaskCompleteCircle>
        ) : (
          <TaskIncompleteCircle
            onClick={toggleProjectTaskStatus}
            disabled={disabled}
          />
        )}

        <TextContainer>
          <TextRow>
            {/* Using a div here rather than textinput as multiline support is needed */}
            <TaskText
              ref={(node) => {
                textRef.current = node;
              }}
              suppressContentEditableWarning
              contentEditable={userCanEdit.toString()}
              role="textbox"
              aria-multiline="true"
              onBlur={onTextBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && task.completed) {
                  textRef.current.blur();
                  return;
                }

                // Enter will create a new task
                // Shift + Enter will create a new line
                if (e.key === 'Enter' && !e.shiftKey) {
                  setIsEnterPressed(true);
                  e.preventDefault();
                }
              }}
              onFocus={() => setInputIsFocused(true)}
            >
              {task.details}
            </TaskText>
            <ButtonContainer>
              {buttonsVisible && !task.isNew ? (
                <ButtonIconCondensed
                  id="delete-button"
                  iconName="delete"
                  size="small"
                  onClick={onDeletePress}
                  accessbilityLabel={translate('common.delete')}
                  onBlur={hideButtons} // For keyboard accessibility
                />
              ) : null}
            </ButtonContainer>
          </TextRow>

          {task.completedBy ? (
            <CompletedByInfo
              completed={task.completed}
              completedByUserName={task.completedBy?.name}
              completedAt={task.completedAt}
              completedByCompany={task.completedBy?.company}
            />
          ) : null}
        </TextContainer>
      </TaskRow>

      <ProjectTaskAssets
        taskAssets={[...taskImages, ...taskVideos]}
        onTaskAssetClick={onTaskAssetClick}
      />
    </TaskContainer>
  );
};

ProjectTask.defaultProps = {
  disabled: false,
  isPhotoView: false,
};

ProjectTask.propTypes = {
  disabled: PropTypes.bool,
  index: PropTypes.number,
  task: PropTypes.shape({
    id: PropTypes.string,
    completed: PropTypes.bool,
    details: PropTypes.string,
    images: PropTypes.shape({
      nodes: PropTypes.array,
    }),
    videos: PropTypes.shape({
      nodes: PropTypes.array,
    }),
    completedBy: PropTypes.object,
    completedAt: PropTypes.string,
    isNew: PropTypes.bool,
    position: PropTypes.number,
  }).isRequired,
  isPhotoView: PropTypes.bool,
  onTaskAssetClick: PropTypes.func,
  createProjectTask: PropTypes.func,
  addNewTask: PropTypes.func,
  removeNewTask: PropTypes.func,
};

export default ProjectTask;
